<template>
  <div v-if="isVisible" class="modal-overlay" @click="closeModal">
    <div class="modal-container" @click.stop>
      <div class="modal-header">
        <h3 class="modal-title">{{$t('add_new_card')}}</h3>
        <button class="close-button" @click="closeModal">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6.55323 6.37557L13.6243 13.4466C13.6456 13.468 13.6588 13.4988 13.6588 13.535C13.6588 13.5713 13.6456 13.6021 13.6243 13.6234C13.603 13.6447 13.5722 13.6579 13.5359 13.6579C13.4996 13.6579 13.4688 13.6447 13.4475 13.6234L6.37646 6.55235C6.35514 6.53103 6.34193 6.50023 6.34193 6.46396C6.34193 6.4277 6.35514 6.39689 6.37646 6.37557C6.39777 6.35426 6.42858 6.34105 6.46484 6.34105C6.50111 6.34105 6.53191 6.35426 6.55323 6.37557Z" fill="#282A36" stroke="#282A36"/>
            <path d="M13.6235 6.37557C13.6449 6.39689 13.6581 6.4277 13.6581 6.46396C13.6581 6.50023 13.6449 6.53103 13.6235 6.55235L6.55248 13.6234C6.53116 13.6447 6.50035 13.6579 6.46409 13.6579C6.42782 13.6579 6.39702 13.6447 6.3757 13.6234C6.35438 13.6021 6.34117 13.5713 6.34117 13.535C6.34117 13.4988 6.35438 13.468 6.3757 13.4466L13.4468 6.37557C13.4681 6.35426 13.4989 6.34105 13.5352 6.34105C13.5714 6.34105 13.6022 6.35426 13.6235 6.37557Z" fill="#282A36" stroke="#282A36"/>
          </svg>
        </button>
      </div>

      <div class="modal-content">
        <form v-if="!show3DModal" @submit.prevent="saveCard">
          <div class="form-group">
            <label>{{$t('card_name_title')}}</label>
            <input
                type="text"
                v-model="cardData.cardAlias"
                :placeholder="this.$t('card_description')"
                class="form-input"
                :disabled="isProcessing"
            >
          </div>

          <div class="form-group">
            <label>{{$t('card_holder_name')}}</label>
            <input
                type="text"
                v-model="cardData.cardHolderName"
                :placeholder="this.$t('name_on_card')"
                class="form-input"
                style="text-transform: uppercase;"
                :disabled="isProcessing"
            >
          </div>

          <CreditCardInput
              v-model="cardData.cardNumber"
              @update:rawValue="cardData.cardNumberRaw = $event"
              @card-type-changed="handleCardTypeChanged"
              :disabled="isProcessing"
          />

          <div class="form-row">
            <div class="form-group">
              <label>{{$t('expiry_date')}}</label>
              <input
                  type="text"
                  v-model="cardData.expiryDate"
                  :placeholder="this.$t('enter_date')"
                  class="form-input"
                  maxlength="5"
                  @input="formatExpiryDate"
                  :disabled="isProcessing"
              >
            </div>
            <div class="form-group">
              <label>{{$t('security_code')}}</label>
              <input
                  type="text"
                  v-model="cardData.cvv"
                  :placeholder="$t('security_code_placeholder')"
                  class="form-input"
                  maxlength="3"
                  :disabled="isProcessing"
              >
            </div>
          </div>

          <div class="form-actions">
            <button type="submit" class="save-button" :disabled="isProcessing">
              <div v-if="isProcessing" class="loading-spinner"></div>
              {{ isProcessing ? $t('processing') : $t('add_new_card') }}
            </button>
          </div>
        </form>

        <div v-if="show3DModal && iframe3DUrl" class="threeds-container">
          <div class="threeds-header">
            <h4>{{$t('3d_secure_verification')}}</h4>
            <p>{{$t('3d_secure_description')}}</p>
          </div>

          <div class="threeds-iframe-wrapper">
            <iframe
                :src="iframe3DUrl"
                width="100%"
                height="600"
                frameborder="0"
                :title="$t('3d_secure_verification')"
                @load="on3DIframeLoad"
                sandbox="allow-same-origin allow-scripts allow-forms allow-top-navigation allow-popups"
            ></iframe>
          </div>

          <div class="threeds-loading" v-if="is3DLoading">
            <div class="loading-spinner"></div>
            <p>{{$t('verification_processing')}}</p>
          </div>
        </div>

        <div v-if="!show3DModal" class="info-message">
          <svg width="20" height="20" viewBox="0 0 14 13" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M7 12.75C10.4518 12.75 13.25 9.95178 13.25 6.5C13.25 3.04822 10.4518 0.25 7 0.25C3.54822 0.25 0.75 3.04822 0.75 6.5C0.75 9.95178 3.54822 12.75 7 12.75ZM8.11584 9.70858L8.21565 9.3006C8.16399 9.32486 8.0807 9.35258 7.96649 9.38414C7.85197 9.4157 7.7489 9.43177 7.65831 9.43177C7.46536 9.43177 7.32952 9.40014 7.25065 9.33657C7.17236 9.273 7.13331 9.15342 7.13331 8.97827C7.13331 8.90887 7.14509 8.80542 7.16973 8.67003C7.19361 8.53373 7.22108 8.41261 7.25161 8.30666L7.62419 6.98759C7.66068 6.86653 7.68571 6.73344 7.69916 6.58818C7.71292 6.44325 7.71932 6.34185 7.71932 6.28429C7.71932 6.00614 7.62182 5.78041 7.42676 5.60629C7.2317 5.43229 6.95393 5.34529 6.59396 5.34529C6.39365 5.34529 6.18188 5.38088 5.95776 5.45201C5.73363 5.52294 5.49933 5.6084 5.25421 5.7082L5.15415 6.1165C5.22719 6.08949 5.31419 6.06043 5.41598 6.03034C5.51732 6.00038 5.61674 5.98489 5.71347 5.98489C5.91096 5.98489 6.04399 6.01856 6.1137 6.08488C6.18342 6.15139 6.21844 6.2697 6.21844 6.43883C6.21844 6.53236 6.20736 6.63626 6.18438 6.74919C6.16172 6.86282 6.13342 6.98298 6.10013 7.1098L5.72595 8.43419C5.69266 8.57336 5.66834 8.69787 5.65304 8.80843C5.63786 8.91886 5.63057 9.02724 5.63057 9.13262C5.63057 9.40482 5.73114 9.6292 5.93222 9.80627C6.13329 9.98264 6.41523 10.0714 6.77769 10.0714C7.01372 10.0714 7.22088 10.0406 7.39917 9.97854C7.57727 9.9167 7.81644 9.82675 8.11584 9.70858ZM8.04946 4.3502C8.22352 4.18882 8.31014 3.99254 8.31014 3.76272C8.31014 3.53341 8.22365 3.33675 8.04946 3.17331C7.87584 3.01032 7.66657 2.92857 7.4219 2.92857C7.17646 2.92857 6.96635 3.01013 6.79107 3.17331C6.61579 3.33675 6.52796 3.53334 6.52796 3.76272C6.52796 3.99254 6.61579 4.18875 6.79107 4.3502C6.96667 4.51217 7.17639 4.59321 7.4219 4.59321C6.66664 4.59321 7.87584 4.51217 8.04946 4.3502Z" fill="#D0D5DD"/>
          </svg>
          <span>{{$t('pricing_security_description')}}.</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import pricingService from '@/service/pricingService';
import CreditCardInput from '@/components/CreditCardInput.vue';

export default {
  name: 'PricingAddNewCardModal',
  components: {
    CreditCardInput
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    skip3DSecure: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      cardData: {
        cardNumber: '',
        cardNumberRaw: '',
        expiryDate: '',
        cvv: '',
        cardAlias: '',
        cardHolderName: ''
      },
      detectedCardType: null,
      isProcessing: false,
      show3DModal: false,
      iframe3DUrl: null,
      is3DLoading: false,
      paymentResult: null,
      currentOrderId: null
    }
  },

  computed: {
    cardType() {
      if (this.detectedCardType) {
        return this.detectedCardType;
      }

      const number = (this.cardData?.cardNumberRaw || this.cardData?.cardNumber?.replace(/\s/g, '')) || '';
      if (number.startsWith('4')) {
        return 'visa';
      } else if (number.startsWith('5') || number.startsWith('2')) {
        return 'mastercard';
      }
      return null;
    }
  },

  mounted() {

    const urlParams = new URLSearchParams(window.location.search);
    const cardAdd = urlParams.get('card_add');

    if (cardAdd === 'true') {
      this.$snackbar.add({
        text: this.$t('card_added_success'),
        type: 'success'
      });
    } else if (cardAdd === 'false') {
      const error = urlParams.get('error');
      this.$snackbar.add({
        text: error || this.$t('card_add_failed'),
        type: 'error'
      });
    }
  },

  methods: {
    closeModal() {
      if (this.show3DModal && this.is3DLoading) {
        const confirmClose = confirm(this.$t('3d_verification_in_progress'));
        if (!confirmClose) return;
      }

      this.resetModal();
      this.$emit('close');
    },

    resetModal() {
      this.show3DModal = false;
      this.iframe3DUrl = null;
      this.isProcessing = false;
      this.is3DLoading = false;
      this.paymentResult = null;
      this.currentOrderId = null;
      this.resetForm();
    },

    handleCardTypeChanged(cardType) {
      this.detectedCardType = cardType || null;
    },


    handle3DResult(data) {
      this.is3DLoading = false;
      this.show3DModal = false;
      this.iframe3DUrl = null;

      if (data.success) {
        this.paymentResult = {
          success: true,
          cardId: data.cardId,
          maskedCard: data.maskedCard,
          last4: data.last4,
          bin: data.bin,
          message: this.$t('card_added_success')
        };

        this.$snackbar.add({
          text: this.$t('card_added_success'),
          type: 'success'
        });

        this.$emit('save', {
          uuid: data.cardId,
          maskedCard: data.maskedCard,
          success: true
        });

        setTimeout(() => {
          this.closeModal();
        }, 3000);

      } else {
        this.paymentResult = {
          success: false,
          error: data.error || this.$t('unknown_error'),
          orderId: data.orderId
        };

        this.$snackbar.add({
          text: data.error || this.$t('card_add_failed'),
          type: 'error'
        });
      }

      this.isProcessing = false;
    },

    async saveCard() {
      if (!this.validateForm()) {
        return;
      }

      this.isProcessing = true;
      this.paymentResult = null;

      try {
        const expiryParts = (this.cardData?.expiryDate || '').split('/');
        const month = expiryParts[0];
        const year = expiryParts[1];

        if (!month || !year) {
          this.$snackbar.add({
            text: this.$t('enter_valid_expiry_date'),
            type: 'error'
          });
          this.isProcessing = false;
          return;
        }

        const requestData = {
          cardAlias: this.cardData?.cardAlias || '',
          cardHolderName: this.cardData?.cardHolderName || '',
          cardNumber: this.cardData?.cardNumberRaw || '',
          expiryMonth: parseInt(month, 10) || 0,
          expiryYear: parseInt(`20${year}`, 10) || 0,
          cvv: this.cardData?.cvv || '',
          isPrimary: false,
          open3d: !this.skip3DSecure
        };

        const response = await pricingService.addCompanyCard(requestData);

        if (response?.data?.responseType === 'success') {
          const cardId = response?.data?.data?.cardId || response?.data?.cardId;
          const iframeUrl = response?.data?.data?.iframeUrl;

          if (cardId) {
            // Kart başarıyla eklendi (3DS atlandi)
            this.paymentResult = {
              success: true,
              cardId: cardId,
              message: this.$t('card_added_success')
            };

            this.$snackbar.add({
              text: this.$t('card_added_success'),
              type: 'success'
            });

            this.$emit('save', {
              uuid: cardId,
              success: true
            });

            setTimeout(() => {
              this.closeModal();
            }, 1000);
          } else if (iframeUrl) {
            // 3D Secure: tam sayfa yonlendirme (Garanti ile ayni akis)
            window.location.href = iframeUrl;
          }
        } else {
          this.$snackbar.add({
            text: response?.data?.message || this.$t('card_add_failed'),
            type: 'error'
          });
          this.isProcessing = false;
        }

      } catch (error) {
        console.error('Kart ekleme hatası:', error);
        this.$snackbar.add({
          text: error?.response?.data?.message || this.$t('card_add_failed'),
          type: 'error'
        });
        this.isProcessing = false;
      }
    },

    on3DIframeLoad() {
      this.is3DLoading = false;
    },

    validateForm() {
      if (!this.cardData?.cardAlias?.trim()) {
        this.$snackbar.add({
          text: this.$t('card_name_required'),
          type: 'error'
        });
        return false;
      }

      if (!this.cardData?.cardHolderName?.trim()) {
        this.$snackbar.add({
          text: this.$t('card_holder_name_required'),
          type: 'error'
        });
        return false;
      }

      if (!this.cardData?.cardNumberRaw || this.cardData.cardNumberRaw.length < 15) {
        this.$snackbar.add({
          text: this.$t('enter_valid_card_number'),
          type: 'error'
        });
        return false;
      }

      const expiryParts = (this.cardData?.expiryDate || '').split('/');
      if (expiryParts.length !== 2) {
        this.$snackbar.add({
          text: this.$t('enter_valid_expiry_date'),
          type: 'error'
        });
        return false;
      }

      const month = parseInt(expiryParts[0], 10);
      const year = parseInt(expiryParts[1], 10);

      if (month < 1 || month > 12) {
        this.$snackbar.add({
          text: this.$t('invalid_month'),
          type: 'error'
        });
        return false;
      }

      const currentYear = new Date().getFullYear() % 100;
      const currentMonth = new Date().getMonth() + 1;

      if (year < currentYear || (year === currentYear && month < currentMonth)) {
        this.$snackbar.add({
          text: this.$t('card_expired'),
          type: 'error'
        });
        return false;
      }

      if (!this.cardData?.cvv || this.cardData.cvv.length < 3) {
        this.$snackbar.add({
          text: this.$t('enter_valid_cvv'),
          type: 'error'
        });
        return false;
      }

      return true;
    },

    formatExpiryDate() {
      if (!this.cardData?.expiryDate) return;

      let value = this.cardData.expiryDate.replace(/\D/g, '');
      if (value.length >= 2) {
        value = value.substring(0, 2) + '/' + value.substring(2, 4);
      }
      this.cardData.expiryDate = value;
    },

    resetForm() {
      this.cardData = {
        cardNumber: '',
        cardNumberRaw: '',
        expiryDate: '',
        cvv: '',
        cardAlias: '',
        cardHolderName: ''
      };
      this.detectedCardType = null;
    },

    async loadExistingCards() {
      try {
        const response = await pricingService.getPaymentMethods();
        if (response?.data) {
        }
      } catch (error) {
      }
    }
  }
}
</script>

<style scoped>
.threeds-container {
  padding: 20px 0;
}

.threeds-header {
  text-align: center;
  margin-bottom: 20px;
}

.threeds-header h4 {
  font-size: 18px;
  color: #1F2937;
  margin: 0 0 8px 0;
  font-family: 'euclid_semi_bold', sans-serif;
}

.threeds-header p {
  font-size: 14px;
  color: #6B7280;
  margin: 0;
}

.threeds-iframe-wrapper {
  position: relative;
  background: #f9f9f9;
  border-radius: 8px;
  overflow: hidden;
  min-height: 600px;
}

.threeds-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  background: rgba(255, 255, 255, 0.95);
  padding: 20px;
  border-radius: 8px;
  z-index: 10;
}

.loading-spinner {
  width: 24px;
  height: 24px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #1F2937;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 10px auto;
}

.result-message {
  margin: 20px 0;
}

.alert-content strong {
  display: block;
  margin-bottom: 4px;
  font-family: 'euclid_medium', sans-serif;
}

.alert-content p {
  margin: 0;
  opacity: 0.8;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-container {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 530px;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
}

.modal-header {
  display: flex;
  align-items: center;
  border-bottom: 1px solid #E5E7EB;
  padding: 24px 24px 20px 24px;
  justify-content: flex-start;
}

.modal-title {
  font-size: 18px;
  color: #1F2937;
  margin: 0;
  font-family: 'euclid_semi_bold', sans-serif;
}

.close-button {
  position: absolute;
  top: 20px;
  right: 20px;
  background: #f5f5f5;
  border: none;
  cursor: pointer;
  color: #666666;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-button:hover {
  background-color: #F3F4F6;
}

.modal-content {
  padding: 24px;
  width: 100% !important;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
  min-width: 0;
}

.form-group label {
  font-size: 14px;
  color: #374151;
  margin-bottom: 8px;
  font-family: 'euclid_medium', sans-serif;
  text-align: left;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-input {
  width: 100%;
  box-sizing: border-box;
  border: none;
  outline: none;
  font-family: euclid_regular, sans-serif;
  font-size: 16px;
  background-color: #f5f5f5;
  color: #282a36;
  padding: 15px;
  border-radius: 8px;
  height: 50px;
  transition: all 0.2s;
}

.form-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.form-input::placeholder {
  color: #9CA3AF;
  font-size: 14px;
}

.form-actions {
  margin-top: 32px;
  margin-bottom: 20px;
}

.save-button {
  width: 100%;
  background: #1F2937;
  color: white;
  border: none;
  padding: 16px 24px;
  border-radius: 16px;
  font-size: 16px;
  cursor: pointer;
  font-family: 'euclid_medium', sans-serif;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.save-button:hover:not(:disabled) {
  background: #111827;
}

.save-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.save-button .loading-spinner {
  width: 18px;
  height: 18px;
  border-width: 2px;
  margin: 0 8px 0 0;
}

.info-message {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  padding: 16px;
  background-color: #F9FAFB;
  border-radius: 8px;
  margin-top: 20px;
}

.info-message span {
  font-size: 12px;
  color: #9CA3AF;
  font-family: 'euclid_regular', sans-serif;
}

/* Tablet responsive */
@media (max-width: 768px) {
  .modal-container {
    width: 95%;
    margin: 10px;
    max-height: 90vh;
  }

  .form-row {
    grid-template-columns: 1fr;
    gap: 0;
  }

  .modal-header {
    padding: 16px 20px;
  }

  .modal-content {
    padding: 20px;
  }

  .threeds-iframe-wrapper {
    min-height: 500px;
  }
}

/* Mobile responsive */
@media (max-width: 480px) {
  .modal-overlay {
    align-items: flex-end;
  }

  .modal-container {
    width: 100%;
    max-width: 100%;
    max-height: 92vh;
    margin: 0;
    border-radius: 16px 16px 0 0;
  }

  .modal-header {
    padding: 16px;
    position: sticky;
    top: 0;
    background: white;
    z-index: 2;
    border-radius: 16px 16px 0 0;
  }

  .modal-title {
    font-size: 16px;
  }

  .close-button {
    top: 12px;
    right: 12px;
    padding: 6px;
  }

  .modal-content {
    padding: 16px;
  }

  .form-group {
    margin-bottom: 16px;
  }

  .form-group label {
    font-size: 13px;
    margin-bottom: 6px;
  }

  .form-input {
    font-size: 15px;
    padding: 12px;
    height: 46px;
    border-radius: 8px;
  }

  .form-input::placeholder {
    font-size: 13px;
  }

  .form-row {
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  .form-actions {
    margin-top: 24px;
    margin-bottom: 16px;
  }

  .save-button {
    padding: 14px 20px;
    font-size: 15px;
    border-radius: 12px;
    height: 46px;
  }

  .info-message {
    padding: 12px;
    margin-top: 16px;
  }

  .info-message span {
    font-size: 11px;
  }

  .threeds-iframe-wrapper {
    min-height: 400px;
  }

  .threeds-header h4 {
    font-size: 16px;
  }

  .threeds-header p {
    font-size: 13px;
  }
}
</style>
